import { useEffect, useRef } from "react";
import L from "leaflet";

// Task 9 — carrier-only fuzzy area map.
//
// Never exposes an exact address:
//   • We seed a deterministic ~500 m offset from the post id so the same
//     post always shows the same fuzzy centre (but adjacent posts don't
//     all shift the same way).
//   • The pin is hidden entirely — only a shaded circle draws, so there's
//     no "aim here" cross-hair a bad actor could dead-reckon.
//   • Radius is 1.5 km — big enough to swallow a residential street plus
//     its neighbours, so the exact house is impossible to infer even if
//     someone knew the postcode.
//   • The tile layer + attribution match the existing `<MapView>` so we
//     don't leak a different map provider.
//
// Access is enforced by the caller (WasteDetail wraps this in a
// `user?.role === "carrier"` check). The customer never sees it; the
// admin still sees the precise `<MapView>` alongside their existing tools.
export default function FuzzyAreaMap({
  lat,
  lng,
  postId,
  radiusMeters = 500,
  height = "h-[200px]",
  testId = "fuzzy-area-map",
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || mapRef.current || lat == null || lng == null) return;
    // Deterministic offset in ±0.005° range (~500 m at UK latitudes).
    // Uses the post id so the same post is always in the same fuzzy spot.
    const seed = (postId || "").split("").reduce((a, c) => a * 33 + c.charCodeAt(0), 5381) >>> 0;
    const rand = (n) => {
      // xorshift on the seed byte-offset — cheap PRNG, keeps the map
      // self-contained without pulling a lib in for one component.
      let x = (seed ^ (n * 2654435761)) >>> 0;
      x ^= x << 13; x >>>= 0;
      x ^= x >> 17;
      x ^= x << 5; x >>>= 0;
      return ((x % 10000) / 10000 - 0.5) * 2; // -1..1
    };
    const fuzzyLat = lat + rand(1) * 0.005;
    const fuzzyLng = lng + rand(2) * 0.008;

    const map = L.map(containerRef.current, {
      center: [fuzzyLat, fuzzyLng],
      zoom: 15,
      scrollWheelZoom: false,
      zoomControl: false,
      dragging: false,
      doubleClickZoom: false,
      keyboard: false,
      touchZoom: false,
      boxZoom: false,
    });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: "© OpenStreetMap",
    }).addTo(map);
    L.circle([fuzzyLat, fuzzyLng], {
      radius: radiusMeters,
      color: "#4A6B56",
      weight: 2,
      fillColor: "#4A6B56",
      fillOpacity: 0.22,
    }).addTo(map);
    mapRef.current = map;
    // Mount-once: props are only the initial view.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (lat == null || lng == null) return null;

  return (
    <div
      ref={containerRef}
      data-testid={testId}
      className={`${height} w-full rounded-2xl overflow-hidden border border-[#E2DFD8]`}
    />
  );
}
